import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Car, Plus, Trash2, Cpu } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { OnlineDot } from '@/components/domain/OnlineDot';
import { deleteVehicle } from '@/api/endpoints/vehicles';
import { queryKeys } from '@/api/queryKeys';
import { errorMessage } from '@/api/errors';
import { useDeviceStatus, useDevices, useVehicles } from '@/features/shared/queries';
import { AddVehicleForm } from './AddVehicleForm';
import { ClaimDeviceForm } from '@/features/setup/ClaimDeviceForm';
import type { Vehicle } from '@/types/api';

export function VehiclesPage() {
  const queryClient = useQueryClient();
  const vehicles = useVehicles();
  const devices = useDevices();
  const status = useDeviceStatus();
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [linkingDevice, setLinkingDevice] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Vehicle | null>(null);

  const removal = useMutation({
    mutationFn: (vehicleId: number) => deleteVehicle(vehicleId),
    onSuccess: async () => {
      setPendingDelete(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.vehicles });
      await queryClient.invalidateQueries({ queryKey: queryKeys.summary });
      toast.success('Vehicle removed.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const deviceList = devices.data ?? [];
  const statusById = new Map((status.data ?? []).map((entry) => [entry.id, entry]));
  const unattached = deviceList.filter((device) => device.vehicle_id === null);

  return (
    <>
      <PageHeader
        title="Vehicles"
        description="Each vehicle carries one AutoAware device."
        action={
          <div className="flex gap-2">
            <Button onClick={() => setLinkingDevice(true)}>
              <Cpu className="size-4" aria-hidden />
              Link device
            </Button>
            <Button variant="primary" onClick={() => setAddingVehicle(true)}>
              <Plus className="size-4" aria-hidden />
              Add vehicle
            </Button>
          </div>
        }
      />

      {vehicles.isError && <ErrorState error={vehicles.error} onRetry={vehicles.refetch} />}

      <Card>
        <CardHeader title="Your vehicles" />
        {vehicles.isPending ? (
          <SkeletonRows rows={3} />
        ) : (vehicles.data ?? []).length === 0 ? (
          <EmptyState
            icon={Car}
            title="No vehicles yet"
            description="Add the vehicle your AutoAware device is fitted to."
            action={
              <Button variant="primary" size="sm" onClick={() => setAddingVehicle(true)}>
                Add your first vehicle
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {(vehicles.data ?? []).map((vehicle) => {
              const device = deviceList.find((entry) => entry.vehicle_id === vehicle.id);
              const live = device ? statusById.get(device.id) : undefined;

              return (
                <li key={vehicle.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-ink">{vehicle.license_plate}</p>
                    <p className="mt-0.5 text-[13px] text-ink-secondary">{vehicle.vehicle_type}</p>
                  </div>

                  <div className="min-w-0 flex-1">
                    {device ? (
                      <>
                        <Link
                          to={`/devices/${device.id}`}
                          className="text-[13px] font-medium text-accent hover:text-accent-hover"
                        >
                          {device.serial_number}
                        </Link>
                        <div className="mt-0.5">
                          <OnlineDot online={live?.is_online ?? false} />
                        </div>
                      </>
                    ) : (
                      <p className="text-[13px] text-ink-muted">No device attached</p>
                    )}
                  </div>

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setPendingDelete(vehicle)}
                    aria-label={`Remove ${vehicle.license_plate}`}
                  >
                    <Trash2 className="size-4" aria-hidden />
                    Remove
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {unattached.length > 0 && (
        <Card className="mt-4">
          <CardHeader
            title="Devices not attached to a vehicle"
            description="These are linked to your account but not assigned. Attach one by linking it again with a vehicle selected."
          />
          <CardBody>
            <ul className="space-y-1 text-[13px]">
              {unattached.map((device) => (
                <li key={device.id}>
                  <Link to={`/devices/${device.id}`} className="text-accent hover:text-accent-hover">
                    {device.serial_number}
                  </Link>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <Dialog
        open={addingVehicle}
        onOpenChange={setAddingVehicle}
        title="Add a vehicle"
        description="Registration number and type."
      >
        <AddVehicleForm onAdded={() => setAddingVehicle(false)} />
      </Dialog>

      <Dialog
        open={linkingDevice}
        onOpenChange={setLinkingDevice}
        title="Link a device"
        description="Use the details printed on the card that came with your device."
      >
        <ClaimDeviceForm vehicles={vehicles.data ?? []} onClaimed={() => setLinkingDevice(false)} />
      </Dialog>

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Remove this vehicle?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={removal.isPending}
              onClick={() => pendingDelete && removal.mutate(pendingDelete.id)}
            >
              Remove vehicle
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-secondary">
          <span className="font-medium text-ink">{pendingDelete?.license_plate}</span> will no longer
          be monitored. Crash records already recorded against it are kept — they may be legally
          significant and are never deleted here.
        </p>
      </Dialog>
    </>
  );
}
